import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, RotateCcw, XCircle } from "lucide-react";
import { useEffect, useRef } from "react";

interface UnsubscribePageProps {
  token: string;
}

/** Veřejná stránka "byl jste odhlášen" + button na resubscribe. */
export function UnsubscribePage({ token }: UnsubscribePageProps) {
  const unsubMutation = trpc.hca.klienti.unsubscribe.useMutation();
  const resubMutation = trpc.hca.klienti.resubscribe.useMutation();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    unsubMutation.mutate({ token });
  }, [token, unsubMutation]);

  const unsubSuccess = unsubMutation.isSuccess && !resubMutation.isSuccess;
  const resubSuccess = resubMutation.isSuccess;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-12">
      <Card className="w-full max-w-lg" data-testid="hca-unsubscribe-page">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          {unsubMutation.isPending || unsubMutation.isIdle ? (
            <div className="w-full space-y-3">
              <Skeleton className="mx-auto h-12 w-12 rounded-full" />
              <Skeleton className="mx-auto h-6 w-3/4" />
              <Skeleton className="mx-auto h-4 w-1/2" />
            </div>
          ) : unsubMutation.isError ? (
            <>
              <XCircle
                className="h-12 w-12 text-destructive"
                aria-hidden="true"
                data-testid="hca-unsubscribe-error-icon"
              />
              <h1 className="text-2xl font-bold">Odhlášení selhalo</h1>
              <p className="text-muted-foreground" data-testid="hca-unsubscribe-error-message">
                {unsubMutation.error.message}
              </p>
              <Button variant="outline" asChild data-testid="hca-unsubscribe-home-button">
                <Link to="/" data-testid="hca-unsubscribe-home-link">
                  Zpět na úvodní stránku
                </Link>
              </Button>
            </>
          ) : resubSuccess ? (
            <>
              <CheckCircle2
                className="h-12 w-12 text-primary"
                aria-hidden="true"
                data-testid="hca-resubscribe-success-icon"
              />
              <h1 className="text-2xl font-bold" data-testid="hca-resubscribe-title">
                Odběr obnoven
              </h1>
              <p className="text-muted-foreground" data-testid="hca-resubscribe-message">
                {resubMutation.data?.jmeno
                  ? `${resubMutation.data.jmeno}, budete dostávat marketingové zprávy i nadále.`
                  : "Budete dostávat marketingové zprávy i nadále."}
              </p>
              <Button variant="outline" asChild data-testid="hca-resubscribe-home-button">
                <Link to="/" data-testid="hca-resubscribe-home-link">
                  Zpět na úvodní stránku
                </Link>
              </Button>
            </>
          ) : unsubSuccess ? (
            <>
              <CheckCircle2
                className="h-12 w-12 text-primary"
                aria-hidden="true"
                data-testid="hca-unsubscribe-success-icon"
              />
              <h1 className="text-2xl font-bold" data-testid="hca-unsubscribe-title">
                {unsubMutation.data?.alreadyUnsubscribed
                  ? "Už jste byli odhlášeni"
                  : "Byl jste odhlášen"}
              </h1>
              <p className="text-muted-foreground" data-testid="hca-unsubscribe-message">
                {unsubMutation.data?.jmeno
                  ? `${unsubMutation.data.jmeno}, přestali jsme vám zasílat marketingovou komunikaci.`
                  : "Přestali jsme vám zasílat marketingovou komunikaci."}{" "}
                Důležité provozní zprávy týkající se vašich přihlášek vám posílat nadále budeme.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resubMutation.isPending}
                  onClick={() => resubMutation.mutate({ token })}
                  data-testid="hca-unsubscribe-resub-button"
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {resubMutation.isPending ? "Obnovuji…" : "Rozmyslel jsem si to"}
                </Button>
                <Button variant="ghost" size="sm" asChild data-testid="hca-unsubscribe-home-button">
                  <Link to="/" data-testid="hca-unsubscribe-home-link">
                    Zpět na úvodní stránku
                  </Link>
                </Button>
              </div>
              {resubMutation.isError && (
                <p
                  className="text-xs text-destructive"
                  role="alert"
                  data-testid="hca-unsubscribe-resub-error"
                >
                  {resubMutation.error.message}
                </p>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
